/**
 * services/TradeExecutionEventService.ts
 * Append-only service for the trade execution event log
 *
 * TradeExecutionEvent is the *canonical record* for all execution lifecycle actions.
 * TradeExecution is the derived current-state projection; it is updated by each event.
 *
 * Design constraints (enforced here):
 * - Events are IMMUTABLE after write. update() throws unconditionally.
 * - appendEvent() is a write-within-transaction method: callers MUST call it inside
 *   an active Dexie 'rw' transaction that includes db.trade_execution_events,
 *   db.sync_operations, and db.sync_state. Dexie auto-propagates the transaction.
 * - appendEvent() is idempotent: if an event with the same operationId already exists,
 *   it returns the existing event without writing a duplicate.
 *
 * §8.4 Atomic write contract — the caller (TradePromotionService / future action handlers)
 * is responsible for the full three-step sequence inside a single transaction:
 *   1. appendEvent()  ← this service
 *   2. TradeExecutionService.updateProjectionFromEvent()
 *   3. BalanceHistoryService.addExecutionTransaction() — only for financial-impact events
 *
 * @see TradeExecution — projection model updated after each event
 * @see TradeExecutionEventType — financial impact notes per event type
 */

import { BaseService } from './BaseService';
import { isTombstoned } from '@/utils/tombstone';
import { generateUUID } from '@/utils/uuid';
import { markAsInstrumentedWriteContext } from './sync/SyncImportContext';
import { getSyncOperationService } from './sync/SyncOperationService';
import { buildSetOps } from './sync/OperationBuilder';
import type { TradeExecutionEvent } from '@/types/schema/TradeExecutionEvent';

/**
 * Input type for appending a new event.
 * Callers supply all domain fields; id/createdAt/updatedAt are generated by the service.
 */
export type AppendEventInput = Omit<TradeExecutionEvent, 'id' | 'createdAt' | 'updatedAt'>;

/**
 * Service for the trade execution event log
 *
 * Events are append-only and immutable. The only write path is appendEvent().
 * Inherits get(), getAll(), softDelete() from BaseService for sync/tombstone flows.
 * update() is overridden to throw — event content must never change after write.
 */
export class TradeExecutionEventService extends BaseService<TradeExecutionEvent> {
  constructor() {
    super('trade_execution_events');
  }

  // ===== APPEND =====

  /**
   * Append an execution event within the caller's active Dexie transaction.
   *
   * IMPORTANT: Must be called inside an active Dexie 'rw' transaction that includes
   * db.trade_execution_events, db.sync_operations, and db.sync_state. Dexie
   * auto-propagates the active transaction — no explicit tx passing required.
   *
   * Idempotency: if an event with this operationId already exists, the existing
   * record is returned immediately with no writes.
   *
   * @param data - Event fields (id/createdAt/updatedAt are generated)
   * @returns The created event, or the existing event if operationId was already used
   */
  async appendEvent(data: AppendEventInput): Promise<TradeExecutionEvent> {
    // Idempotency check — return existing event if operationId already consumed
    const existing = await this.table
      .where('operationId')
      .equals(data.operationId)
      .first();
    if (existing) {
      return existing;
    }

    const now = new Date().toISOString();
    const event: TradeExecutionEvent = {
      ...data,
      id: generateUUID(),
      createdAt: now,
      updatedAt: now,
    };

    // Mark the active transaction as instrumented to suppress safety-net hook warnings.
    // Called here (not in add()) because this method expects to run inside a
    // caller-supplied transaction, not one opened by BaseService.
    markAsInstrumentedWriteContext();

    await this.table.add(event);

    // Log set ops for sync (all fields on creation)
    const ops = buildSetOps(
      'trade_execution_events',
      event.id,
      event as unknown as Record<string, unknown>
    );
    if (ops.length > 0) {
      await getSyncOperationService().logBatch(ops);
    }

    return event;
  }

  // ===== IMMUTABILITY GUARDS =====
  //
  // Events are append-only. The only valid write path is appendEvent().
  // All inherited mutators are sealed to prevent accidental direct writes
  // that bypass the §8.4 atomic contract (append → projection → balance).

  /**
   * Sealed — use appendEvent() instead.
   * @throws Always
   */
  override async add(_data: Partial<TradeExecutionEvent>): Promise<TradeExecutionEvent> {
    throw new Error(
      'TradeExecutionEventService.add() is sealed. Use appendEvent() to write execution events.'
    );
  }

  /**
   * Events are immutable after write. This method always throws.
   *
   * If an event was recorded incorrectly, the correct pattern is:
   * - Append a corrective event (e.g., order-cancelled after order-placed)
   * - Update the TradeExecution projection to reflect the corrective event
   * Events in the log are the permanent historical record and must not be altered.
   *
   * @throws Always — event content is immutable
   */
  override async update(_id: string, _changes: Partial<TradeExecutionEvent>): Promise<TradeExecutionEvent> {
    throw new Error(
      'TradeExecutionEvent records are immutable. Append a corrective event instead.'
    );
  }

  /**
   * Sealed — execution events are the immutable audit log and must not be soft-deleted.
   * @throws Always
   */
  override async softDelete(_id: string): Promise<void> {
    throw new Error(
      'TradeExecutionEvent records cannot be soft-deleted. Events are the immutable audit log.'
    );
  }

  /**
   * Sealed — execution events are the immutable audit log and must not be hard-deleted.
   * @throws Always
   */
  override async hardDelete(_id: string): Promise<void> {
    throw new Error(
      'TradeExecutionEvent records cannot be hard-deleted. Events are the immutable audit log.'
    );
  }

  // ===== QUERIES =====

  /**
   * Get all events for a TradeExecution, ordered by occurredAt ascending.
   *
   * Uses the tradeExecutionId index. Excludes tombstoned records.
   *
   * @param tradeExecutionId - ID of the parent TradeExecution
   * @returns Events in chronological order (occurredAt ascending)
   */
  async getByTradeExecutionId(tradeExecutionId: string): Promise<TradeExecutionEvent[]> {
    try {
      const records = await this.table
        .where('tradeExecutionId')
        .equals(tradeExecutionId)
        .toArray();
      return records
        .filter(r => !isTombstoned(r))
        .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
    } catch (error) {
      console.error(`Failed to get events for tradeExecution ${tradeExecutionId}:`, error);
      throw new Error('Failed to retrieve execution events');
    }
  }

  /**
   * Get all events for a Trade (across all executions), ordered by occurredAt ascending.
   *
   * Uses the tradeId index. Excludes tombstoned records.
   *
   * @param tradeId - ID of the parent Trade
   * @returns Events in chronological order (occurredAt ascending)
   */
  async getByTradeId(tradeId: string): Promise<TradeExecutionEvent[]> {
    try {
      const records = await this.table
        .where('tradeId')
        .equals(tradeId)
        .toArray();
      return records
        .filter(r => !isTombstoned(r))
        .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
    } catch (error) {
      console.error(`Failed to get events for trade ${tradeId}:`, error);
      throw new Error('Failed to retrieve execution events');
    }
  }

  /**
   * Get all events recorded in a session, ordered by occurredAt ascending.
   *
   * Uses the sessionId index. Excludes tombstoned records.
   *
   * @param sessionId - ID of the session
   * @returns Events in chronological order (occurredAt ascending)
   */
  async getBySessionId(sessionId: string): Promise<TradeExecutionEvent[]> {
    try {
      const records = await this.table
        .where('sessionId')
        .equals(sessionId)
        .toArray();
      return records
        .filter(r => !isTombstoned(r))
        .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
    } catch (error) {
      console.error(`Failed to get events for session ${sessionId}:`, error);
      throw new Error('Failed to retrieve execution events');
    }
  }

  /**
   * Get all events within a market-time range (inclusive), ordered by occurredAt ascending.
   *
   * Filters by occurredAt (market time), not createdAt (DB write time).
   * Excludes tombstoned records.
   *
   * @param from - ISO timestamp, inclusive lower bound (occurredAt ≥ from)
   * @param to - ISO timestamp, inclusive upper bound (occurredAt ≤ to)
   * @returns Events in chronological order (occurredAt ascending)
   */
  async getByTimeRange(from: string, to: string): Promise<TradeExecutionEvent[]> {
    try {
      const records = await this.table
        .where('occurredAt')
        .between(from, to, true, true)
        .toArray();
      return records
        .filter(r => !isTombstoned(r))
        .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
    } catch (error) {
      console.error(`Failed to get events for time range ${from}–${to}:`, error);
      throw new Error('Failed to retrieve execution events');
    }
  }
}

// Export singleton instance
export const tradeExecutionEventService = new TradeExecutionEventService();
